/**
 * Service for managing dashboard layout persistence in localStorage
 */

const LAYOUT_STORAGE_KEY = "dashboard-layout";
const LAYOUT_VERSION = "1.0";

/**
 * Get the storage key for a specific user role
 * @param {string} userRole - The user role
 * @returns {string} Storage key
 */
const getStorageKey = (userRole) =>
  `${LAYOUT_STORAGE_KEY}-${userRole}-v${LAYOUT_VERSION}`;

/**
 * Save dashboard layout to localStorage
 * @param {string} userRole - The user role
 * @param {Object} layoutData - Layout data containing widgets and layouts
 */
export const saveDashboardLayout = (userRole, layoutData) => {
  try {
    const storageKey = getStorageKey(userRole);
    const dataToStore = {
      ...layoutData,
      timestamp: Date.now(),
      version: LAYOUT_VERSION,
    };
    localStorage.setItem(storageKey, JSON.stringify(dataToStore));
    console.log("Dashboard layout saved successfully");
  } catch (error) {
    console.error("Failed to save dashboard layout:", error);
  }
};

/**
 * Load dashboard layout from localStorage
 * @param {string} userRole - The user role
 * @returns {Object|null} Saved layout data or null if not found
 */
export const loadDashboardLayout = (userRole) => {
  try {
    const storageKey = getStorageKey(userRole);
    const savedData = localStorage.getItem(storageKey);

    if (!savedData) {
      return null;
    }

    const parsedData = JSON.parse(savedData);

    // Check if the saved data is compatible with current version
    if (parsedData.version !== LAYOUT_VERSION) {
      console.warn("Layout version mismatch, clearing saved layout");
      clearDashboardLayout(userRole);
      return null;
    }

    return parsedData;
  } catch (error) {
    console.error("Failed to load dashboard layout:", error);
    return null;
  }
};

/**
 * Clear saved dashboard layout for a specific user role
 * @param {string} userRole - The user role
 */
export const clearDashboardLayout = (userRole) => {
  try {
    const storageKey = getStorageKey(userRole);
    localStorage.removeItem(storageKey);
    console.log("Dashboard layout cleared successfully");
  } catch (error) {
    console.error("Failed to clear dashboard layout:", error);
  }
};

/**
 * Check if a saved layout exists for the user role
 * @param {string} userRole - The user role
 * @returns {boolean} True if saved layout exists
 */
export const hasSavedLayout = (userRole) => {
  try {
    const storageKey = getStorageKey(userRole);
    return localStorage.getItem(storageKey) !== null;
  } catch (error) {
    console.error("Failed to check for saved layout:", error);
    return false;
  }
};

/**
 * Get all saved layouts (for debugging purposes)
 * @returns {Object} All saved layouts
 */
export const getAllSavedLayouts = () => {
  try {
    const layouts = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(LAYOUT_STORAGE_KEY)) {
        layouts[key] = JSON.parse(localStorage.getItem(key));
      }
    }
    return layouts;
  } catch (error) {
    console.error("Failed to get all saved layouts:", error);
    return {};
  }
};
